import { SortOption, YesObject } from '@yes.technology/react-toolkit'
import DataTable, {
  DataTableProps
} from 'site/Renderer/components/DataTables/DataTable'
import useTableMode from 'site/Renderer/components/DataTables/hooks/useTableMode'
import useChangedFieldValues from 'site/Renderer/components/DataTables/hooks/useChangedFieldValues'
import { useEffect, useMemo, useRef } from 'react'
import { updateDisplayText } from 'shared/utils/fields/fields'
import useObjectclass from 'objectclass/shared/hooks/useObjectclass'
import {
  ExtraSelectionAction,
  SelectionAction
} from 'site/Renderer/components/DataTables/types'
import useTableLayouts, {
  TableLayout
} from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import { useTranslation } from 'react-i18n-lite'

type useUpdateSortingProps = {
  tableLayout: TableLayout
  setSortOptions: (options: SortOption[]) => void
}

const useUpdateSorting = ({
  tableLayout,
  setSortOptions
}: useUpdateSortingProps) => {
  const orderBy = tableLayout.defaultOrderBy.map(
    ([key, value]) => ({ [key]: value }) as SortOption
  )

  const prevOrderBy = useRef({})

  useEffect(() => {
    if (JSON.stringify(prevOrderBy.current) !== JSON.stringify(orderBy)) {
      setSortOptions(orderBy)
      prevOrderBy.current = orderBy
    }
  }, [orderBy, setSortOptions])
}

type RelationClassTableProps = {
  objectclassUuid: string
  tableObjects: YesObject[]
  currentField: string
  currentFieldPersistentSelection?: YesObject[]
  tableId: string
  title: string
  isFetching: boolean
  unsavedObjects: YesObject[]
  setSortOptions: (options: SortOption[]) => void
  setPage: (page: number) => void
  onSave: () => void
  onUnlink: (objects: YesObject[]) => void
} & Pick<
  DataTableProps,
  | 'informational'
  | 'fieldSettings'
  | 'actionButton'
  | 'hierarchyLevel'
  | 'layoutClassificationUuid'
  | 'hideInsertAction'
  | 'selectAndLinkOptions'
  | 'selectAndLinkTargetPath'
  | 'pagination'
  | 'totalObjects'
  | 'sortOptions'
  | 'refetchObjects'
  | 'relationCounterpartField'
  | 'navigationSliderBackgroundColor'
  | 'navigationSliderColor'
  | 'hideSelectAndLinkAction'
>

export const RelationClassTable = ({
  informational,
  objectclassUuid,
  tableObjects,
  currentFieldPersistentSelection,
  fieldSettings,
  tableId,
  title,
  isFetching,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  hideInsertAction,
  hideSelectAndLinkAction,
  selectAndLinkTargetPath,
  selectAndLinkOptions,
  pagination,
  totalObjects,
  relationCounterpartField,
  navigationSliderColor,
  navigationSliderBackgroundColor,
  unsavedObjects,
  sortOptions,
  setSortOptions,
  setPage,
  refetchObjects,
  onSave,
  onUnlink,
  currentField
}: RelationClassTableProps) => {
  const [tableLayout] = useTableLayouts({ layoutClassificationUuid })

  useUpdateSorting({
    tableLayout,
    setSortOptions
  })

  const { objectclass } = useObjectclass({ objectclassUuid })

  const { fields = {} } = objectclass || {}

  const updatedFields = useMemo(
    () => updateDisplayText(fields, fieldSettings),
    [fieldSettings, fields]
  )

  const columns = useMemo(() => Object.keys(updatedFields), [updatedFields])

  const unsavedObjectsUuids =
    currentFieldPersistentSelection?.map(
      (persistentSelection) => persistentSelection?.uuid
    ) || []

  const { t } = useTranslation()

  const saveSelectionAction: SelectionAction = {
    children: t('data-tables.save'),
    onClick: onSave,
    impactClass: 'situationChange',
    pulseAnimate: 'default'
  }

  const primarySelectionAction = unsavedObjects.length
    ? saveSelectionAction
    : undefined

  const extraSelectionActions: ExtraSelectionAction[] = hideSelectAndLinkAction
    ? []
    : [
        {
          children: t('data-tables.unlink'),
          onClick: onUnlink,
          impactClass: 'situationChange'
        }
      ]

  return (
    <DataTable
      informational={informational}
      embedded={true}
      idObjectClass={objectclassUuid}
      objects={tableObjects}
      onSort={setSortOptions}
      setCurrentPage={setPage}
      actionColumnsPosition='last'
      title={title}
      displayLayoutSelection={false}
      hideEditAction
      hasTitleFading
      extendMobileAreaLine
      disableLinkButton
      insertOnly={tableObjects.length === 0}
      highlightedRows={unsavedObjectsUuids}
      relationColumnName={currentField}
      {...{
        objectclass,
        columns,
        fields: updatedFields,
        isLoading: isFetching,
        pagination,
        totalObjects,
        sortOptions,
        actionButton,
        hierarchyLevel,
        refetchObjects,
        relationCounterpartField,
        navigationSliderBackgroundColor,
        navigationSliderColor,
        fieldSettings,
        selectAndLinkOptions,
        primarySelectionAction,
        extraSelectionActions,
        tableLayout,
        hideInsertAction,
        hideSelectAndLinkAction,
        selectAndLinkTargetPath,
        id: tableId
      }}
      {...useTableMode()}
      {...useChangedFieldValues()}
    />
  )
}

export default RelationClassTable
